import React, {FC, useState, useEffect, useCallback} from "react";
import { Button, TopProgress, TableSkeleton } from "../../../components";
import ModalVariables from "../../../components/modal-alerts";
import { billingService } from "../../../api";
import { useTranslation } from "react-i18next";
import { useUi } from "../../../context/ui.context";
import { useNavigate } from "react-router-dom";
import { icons } from "../../../icons";
import { usePageTitle } from "../../../context/navigationContext";
import {
  Container,
  PageTitle,
  ActionsBar,
  GroupCard,
  GroupHeader,
  GroupLeft,
  GroupName,
  GroupDescription,
  GroupActions,
  ActionIconButton,
  ChevronIcon,
  ProductsTable,
  ProductRow,
  ProductCell,
  ProductHeaderRow,
  ProductHeaderCell,
  EmptyGroup,
  ProductCount,
} from "./styles";

const Products:FC = () => {
  const [groups, setGroups] = useState<any[]>([])
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set())
  const [t] = useTranslation()
  usePageTitle(t("PAGE_TITLE_PRODUCTS"));
  const {viewAlert} = useUi()
  const navigate = useNavigate()

  // Modal state
  const [viewModal, setViewModal] = useState<boolean>(false)
  const [activeAnimation, setActiveAnimation] = useState<boolean>(false)
  const [modalType, setModalType] = useState<string | null>(null)
  const [modalTitle, setModalTitle] = useState<string>("")
  const [modalData, setModalData] = useState<any>(null)
  const [modalLoading, setModalLoading] = useState<boolean>(false)
  const [modalComplete, setModalComplete] = useState<boolean>(false)

  const loadGroups = () => {
    billingService.getProductGroups({
      page: 1,
      page_size: 1000,
      ordering: 'name',
    })
    .then((res) => {
      setGroups(res.data.objects || [])
      setPageLoading(false)
    })
    .catch((e) => {
      console.log('error', e);
      setPageLoading(false)
    })
  }

  useEffect(() => {
    loadGroups()
  }, [])

  const toggleGroup = (groupId: number) => {
    setExpandedGroups(prev => {
      const next = new Set(prev)
      if (next.has(groupId)) {
        next.delete(groupId)
      } else {
        next.add(groupId)
      }
      return next
    })
  }

  const openProductGroupModal = (group?: any) => {
    setModalType('product-group-edit')
    setModalTitle(group ? t("EDIT_PRODUCT_GROUP") as string : t("CREATE_PRODUCT_GROUP") as string)
    setModalData(group || {})
    setViewModal(true)
    setActiveAnimation(true)
  }

  const openDeleteGroupModal = (group: any) => {
    setModalType('delete-group')
    setModalTitle(t("DELETE_PRODUCT_GROUP") as string)
    setModalData(group)
    setViewModal(true)
    setActiveAnimation(true)
  }

  const openDeleteProductModal = (product: any) => {
    setModalType('delete-product')
    setModalTitle(t("DELETE_PRODUCT") as string)
    setModalData(product)
    setViewModal(true)
    setActiveAnimation(true)
  }

  const closeModalCancel = useCallback(() => {
    setViewModal(false)
    setActiveAnimation(false)
    setModalType(null)
    setModalTitle("")
    setModalData(null)
    setModalLoading(false)
    setModalComplete(false)
  }, [])

  const handleModalSuccess = useCallback(async (payload: any) => {
    setModalLoading(true)
    try {
      if (modalType === 'product-group-edit') {
        if (payload.id) {
          await billingService.updateProductGroup(payload.id, {
            name: payload.name,
            description: payload.description,
          })
          viewAlert({ severity: 'success', message: t("PRODUCT_GROUP_UPDATED") })
        } else {
          await billingService.createProductGroup({
            name: payload.name,
            description: payload.description,
          })
          viewAlert({ severity: 'success', message: t("PRODUCT_GROUP_CREATED") })
        }
        setModalComplete(true)
        loadGroups()
        setTimeout(() => closeModalCancel(), 400)
      } else if (modalType === 'delete-group') {
        await billingService.deleteProductGroup(modalData.id)
        viewAlert({ severity: 'success', message: t("PRODUCT_GROUP_DELETED") })
        setModalComplete(true)
        loadGroups()
        setTimeout(() => closeModalCancel(), 400)
      } else if (modalType === 'delete-product') {
        await billingService.deleteProduct(modalData.id)
        viewAlert({ severity: 'success', message: t("PRODUCT_DELETED") })
        setModalComplete(true)
        loadGroups()
        setTimeout(() => closeModalCancel(), 400)
      }
    } catch (e: any) {
      viewAlert({
        severity: 'error',
        message: e?.response?.data?.detail || e?.response?.data?.non_field_errors?.[0] || t("ERROR_OCCURRED")
      })
      setModalComplete(false)
    } finally {
      setModalLoading(false)
    }
  }, [modalType, modalData, closeModalCancel, t, viewAlert])

  const getAlertSubTitle = () => {
    if (modalType === 'delete-group') {
      return `${t("DELETE_PRODUCT_GROUP_CONFIRM")} ${modalData?.name}?`
    }
    if (modalType === 'delete-product') {
      return `${t("DELETE_PRODUCT_CONFIRM")} ${modalData?.name}?`
    }
    return ''
  }

  if (pageLoading) {
    return (
      <Container>
        <PageTitle>{t("PRODUCTS")}</PageTitle>
        <TopProgress loading={pageLoading}/>
        <TableSkeleton rows={5} columns={4} columnsData={[]} />
      </Container>
    )
  }

  return (
    <Container>
      <PageTitle>{t("PRODUCTS")}</PageTitle>

      <ActionsBar>
        <Button
          title={t("CREATE_PRODUCT")}
          onClick={() => navigate('/billing/products/create')}
          variant='primary'
          size='display'
        />
        <Button
          title={t("CREATE_PRODUCT_GROUP")}
          onClick={() => openProductGroupModal()}
          variant='stroke'
          size='display'
        />
      </ActionsBar>

      {groups.length === 0 ? (
        <EmptyGroup style={{padding: '20px'}}>{t("NO_PRODUCT_GROUPS")}</EmptyGroup>
      ) : (
        groups.map((group: any) => {
          const isExpanded = expandedGroups.has(group.id)
          const hasProducts = group.products && group.products.length > 0

          return (
            <GroupCard key={group.id}>
              <GroupHeader
                expanded={isExpanded}
                onClick={() => hasProducts && toggleGroup(group.id)}
              >
                <GroupLeft>
                  {hasProducts && (
                    <ChevronIcon expanded={isExpanded} src={icons.Chevron} />
                  )}
                  <GroupName>{group.name}</GroupName>
                  {group.description && (
                    <GroupDescription>({group.description})</GroupDescription>
                  )}
                  <ProductCount>
                    {hasProducts ? `[${group.products.length}]` : ''}
                  </ProductCount>
                </GroupLeft>
                <GroupActions onClick={(e) => e.stopPropagation()}>
                  <ActionIconButton
                    onClick={() => openProductGroupModal(group)}
                    title={t("EDIT_PRODUCT_GROUP") as string}
                  >
                    <img src={icons.Edit} alt="edit" />
                  </ActionIconButton>
                  <ActionIconButton
                    onClick={() => openDeleteGroupModal(group)}
                    disabled={hasProducts}
                    title={hasProducts
                      ? t("CANNOT_DELETE_GROUP_WITH_PRODUCTS") as string
                      : t("DELETE_PRODUCT_GROUP") as string
                    }
                  >
                    <img src={icons.DeleteIcon} alt="delete" />
                  </ActionIconButton>
                </GroupActions>
              </GroupHeader>

              {isExpanded && hasProducts && (
                <ProductsTable>
                  <ProductHeaderRow>
                    <ProductHeaderCell flex="1 0 200px">{t("NAME")}</ProductHeaderCell>
                    <ProductHeaderCell flex="1 0 200px">{t("DESCRIPTION")}</ProductHeaderCell>
                    <ProductHeaderCell flex="0 0 100px">{t("STATUS")}</ProductHeaderCell>
                    <ProductHeaderCell flex="0 0 150px">{t("PRODUCT_CODE")}</ProductHeaderCell>
                    <ProductHeaderCell flex="0 0 200px">{t("PRICE_MODEL")}</ProductHeaderCell>
                    <ProductHeaderCell flex="0 0 80px"></ProductHeaderCell>
                  </ProductHeaderRow>
                  {group.products.map((product: any) => (
                    <ProductRow key={product.id}>
                      <ProductCell
                        flex="1 0 200px"
                        clickable
                        onClick={() => navigate(`/billing/products/${product.id}`)}
                      >
                        {product.name}
                      </ProductCell>
                      <ProductCell flex="1 0 200px">
                        {product.description || '-'}
                      </ProductCell>
                      <ProductCell flex="0 0 100px">
                        {product.status || '-'}
                      </ProductCell>
                      <ProductCell flex="0 0 150px">
                        {product.code || '-'}
                      </ProductCell>
                      <ProductCell flex="0 0 200px">
                        {product.price_model_display || product.price_model || '-'}
                      </ProductCell>
                      <ProductCell flex="0 0 80px">
                        <GroupActions>
                          <ActionIconButton
                            onClick={() => navigate(`/billing/products/${product.id}/edit`)}
                            title={t("EDIT_PRODUCT") as string}
                          >
                            <img src={icons.Edit} alt="edit" />
                          </ActionIconButton>
                          <ActionIconButton
                            onClick={() => openDeleteProductModal(product)}
                            title={t("DELETE_PRODUCT") as string}
                          >
                            <img src={icons.DeleteIcon} alt="delete" />
                          </ActionIconButton>
                        </GroupActions>
                      </ProductCell>
                    </ProductRow>
                  ))}
                </ProductsTable>
              )}
            </GroupCard>
          )
        })
      )}

      <ModalVariables
        modalType={modalType === 'product-group-edit' ? 'product-group-edit' : 'universal' as any}
        viewModal={viewModal}
        activeAnimation={activeAnimation}
        closeModal={handleModalSuccess}
        closeModalCancel={closeModalCancel}
        alertTitle={modalTitle}
        alertSubTitle={getAlertSubTitle()}
        dataToEdit={modalData}
        loading={modalLoading}
        loadingComplete={modalComplete}
      />
    </Container>
  )
}

export default Products
